import { ApolloError, InternalRefetchQueriesInclude } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { LIST_EQL_FIELDS_GQL } from '@modules/common/hooks/useFetchEQLFields';
import { EditMetadataInput } from '@modules/integrationsV2/types';
import {
  ListMetadataKeysDocument,
  useUpdateMetadataMutation,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface UpdateMetadataProps {
  refetchQueries?: InternalRefetchQueriesInclude;
  onError?: (error: ApolloError) => void;
}

export const useUpdateMetadata = ({
  refetchQueries = [],
  onError,
}: UpdateMetadataProps = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const [updateMetadataMutation, { loading, error }] =
    useUpdateMetadataMutation({
      refetchQueries: [
        ListMetadataKeysDocument,
        LIST_EQL_FIELDS_GQL,
        ...refetchQueries,
      ],
      onError: (error) => {
        if (onError) {
          onError(error);
          return;
        }
        showErrorNotification({
          contentComponent:
            'Failed to save changes to metadata. Please try again.',
          props: {},
        });
      },
      onCompleted: () => {
        showSuccessNotification({
          contentComponent:
            'Your edits have been saved! It might take up to 15 mins to reflect them.',
          props: {},
        });
      },
    });

  const updateMetadata = useCallback(
    async (input: EditMetadataInput) => {
      return updateMetadataMutation({
        variables: {
          request: {
            tenantID,
            ...input,
          },
        },
      });
    },
    [tenantID, updateMetadataMutation],
  );

  return [updateMetadata, { isLoading: loading, error }] as const;
};
